import React, { useEffect, useState } from "react";
import { Card, Col, Drawer, Image, Typography, message } from "antd";
import CustomList from "../components/List";
import ListItem from "../components/ListItem";
import CustomPagination from "../components/CustomPagination";
import API from "../../API";

const { Title } = Typography;

// Define column widths and headers - Responsive layout
const activityName = { xxl: 7, xl: 7, lg: 7, md: 7, sm: 12, xs: 12 };
const progress = { xxl: 5, xl: 5, lg: 5, md: 5, sm: 12, xs: 12 };
const remarks = { xxl: 6, xl: 6, lg: 6, md: 6, sm: 0, xs: 0 };
const photo = { xxl: 5, xl: 5, lg: 5, md: 5, sm: 0, xs: 0 };

const headerLayout = [
  { ...activityName, header: "Activity" },
  { ...progress, header: "Progress (%)" },
  { ...remarks, header: "Remarks" },
  { ...photo, header: "Photo" },
];

const ActivitiesSummary = ({ subProjectId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [activeItem, setActiveItem] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const fetchActivities = async (page = 1, size = 15) => {
    try {
      setLoading(true);
      const response = await API.getSubProjectActivitiesProgress(
        subProjectId,
        page,
        size
      );

      if (response.success) {
        setActivities(response.data || []);
        setPagination(response.pagination || null);
      } else {
        message.error("Failed to fetch activities");
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
      message.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subProjectId) {
      fetchActivities(currentPage, pageSize);
    }
  }, [subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [subProjectId]);

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    setActiveItem(null);
    fetchActivities(page, size);
  };

  const handleRowClick = (item) => {
    setActiveItem(item);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setActiveItem(null);
  };

  return (
    <>
      <Card
        bordered={false}
        style={{
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "12px",
          backgroundColor: "#ffffff",
          padding: "24px",
          marginTop: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
          }}
        >
          <Title
            level={4}
            style={{
              marginBottom: 24,
              color: "#0f6788",
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
          >
            Activities Summary
          </Title>
        </div>

        <div className="prst-table">
          <CustomList
            itemName="Activities"
            showToolbar={false}
            items={activities}
            page={currentPage}
            itemCount={pagination?.total || 0}
            loading={loading}
            headerLayout={headerLayout}
            renderListItem={({ item }) => {
              const hasProgress = item.progress_id !== null;
              return (
                <ListItem
                  key={item.activity_id}
                  name={item.activity_name}
                  item={item}
                  isPackage={false}
                  renderActions={() => {}}
                  onClick={() => handleRowClick(item)}
                  style={{
                    backgroundColor:
                      activeItem?.activity_id === item.activity_id
                        ? "#f0f0f0"
                        : "transparent",
                  }}
                >
                  <Col
                    {...activityName}
                    className="contentEllipse"
                    data-label="Activity"
                    title={item.activity_name}
                  >
                    <div style={{ fontWeight: 600, color: "#0f6788" }}>
                      {item.activity_name?.length > 40
                        ? item.activity_name.slice(0, 40) + "..."
                        : item.activity_name || "==//=="}
                    </div>
                    {item.activity_description && (
                      <div style={{ fontSize: "12px", color: "#8c8c8c", marginTop: 4 }}>
                        {item.activity_description?.length > 50
                          ? item.activity_description.slice(0, 50) + "..."
                          : item.activity_description}
                      </div>
                    )}
                  </Col>

                  <Col
                    {...progress}
                    className="contentEllipse"
                    data-label="Progress (%)"
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: hasProgress ? "#52c41a" : "#d9d9d9",
                      }}
                    >
                      {item.progress
                        ? `${parseFloat(item.progress).toFixed(2)}%`
                        : "==//=="}
                    </span>
                  </Col>

                  <Col
                    {...remarks}
                    className="contentEllipse"
                    data-label="Remarks"
                  >
                    {item.remarks?.length > 50 
                      ? item.remarks.slice(0, 50) + "..." 
                      : item.remarks || "==//=="}
                  </Col>

                  <Col {...photo} className="contentEllipse" data-label="Photo">
                    {item.image_url ? (
                      <Image
                        src={item.image_url}
                        alt="Activity"
                        style={{
                          width: 50,
                          height: 50,
                          objectFit: "cover",
                          borderRadius: 4,
                        }}
                        preview={false}
                      />
                    ) : (
                      <div
                        style={{
                          width: 50,
                          height: 50,
                          backgroundColor: "#f0f0f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 4,
                          fontSize: 10,
                          color: "#999",
                        }}
                      >
                        No Image
                      </div>
                    )}
                  </Col>

                  <button
                    className="view-card-details"
                    onClick={() => handleRowClick(item)}
                  >
                    View Details
                  </button>
                </ListItem>
              );
            }}
          />
        </div>

        {pagination && pagination.total > 0 && (
          <CustomPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={loading}
          />
        )}
      </Card>

      <Drawer
        title="Activity Details"
        placement="right"
        closable={true}
        onClose={handleCloseDrawer}
        visible={drawerVisible}
        width={520}
      >
        {activeItem && (
          <>
            <p>
              <strong>Activity Name:</strong> {activeItem.activity_name || "==//=="}
            </p>
            <p>
              <strong>Description:</strong>{" "}
              {activeItem.activity_description || "==//=="}
            </p>
            <p>
              <strong>Progress:</strong>{" "}
              <span
                style={{
                  fontWeight: 600,
                  color: activeItem.progress ? "#52c41a" : "#d9d9d9",
                }}
              >
                {activeItem.progress
                  ? `${parseFloat(activeItem.progress).toFixed(2)}%`
                  : "==//=="}
              </span>
            </p>
            <p>
              <strong>Status:</strong>{" "}
              <span
                style={{
                  padding: "4px 12px",
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  backgroundColor: activeItem.progress_id ? "#f6ffed" : "#fafafa",
                  color: activeItem.progress_id ? "#52c41a" : "#8c8c8c",
                  border: `1px solid ${
                    activeItem.progress_id ? "#b7eb8f" : "#d9d9d9"
                  }`,
                }}
              >
                {activeItem.progress_id ? "Tracked" : "Not Started"}
              </span>
            </p>
            <p>
              <strong>Remarks:</strong> {activeItem.remarks || "==//=="}
            </p>
            <p>
              <strong>Report Start Date:</strong>{" "}
              {activeItem.report_start_date || "==//=="}
            </p>
            <p>
              <strong>Report End Date:</strong>{" "}
              {activeItem.report_end_date || "==//=="}
            </p>
            {activeItem.image_url && (
              <div style={{ marginTop: 16 }}>
                <strong>Photo:</strong>
                <div style={{ marginTop: 8 }}>
                  <Image
                    src={activeItem.image_url}
                    alt="Activity"
                    style={{
                      width: "100%",
                      maxWidth: 400,
                      borderRadius: 8,
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default ActivitiesSummary;

